/**
 * One-shot landing-page banner after an admin-disabled account signs in (PBI #1230).
 *
 * The message is stored in sessionStorage (whitelisted across logout) so it survives
 * the MSAL/local auth teardown that precedes the redirect to `/`.
 */

import { AccountErrorCodes } from '../constants/accountErrorCodes';
import { ACCOUNT_DISABLED_BANNER_KEY } from './sessionCleanupService';

/** Custom event dispatched when any API call returns 403 account_disabled. */
export const ACCOUNT_DISABLED_EVENT = 'account:disabled';

/**
 * Persists the API message for the landing page and dispatches a global event
 * so AuthProvider can tear down MSAL/local auth state.
 */
export function stashAccountDisabledBanner(message: string): void {
  try {
    sessionStorage.setItem(ACCOUNT_DISABLED_BANNER_KEY, message);
  } catch {
    // Best-effort — private browsing may block storage.
  }

  window.dispatchEvent(
    new CustomEvent(ACCOUNT_DISABLED_EVENT, { detail: { message } }),
  );
}

/**
 * Reads and clears the one-shot banner message for the landing page.
 */
export function consumeAccountDisabledBanner(): string | null {
  try {
    const message = sessionStorage.getItem(ACCOUNT_DISABLED_BANNER_KEY);
    if (message) {
      sessionStorage.removeItem(ACCOUNT_DISABLED_BANNER_KEY);
    }
    return message;
  } catch {
    return null;
  }
}

/**
 * Reads errorCode from a 403 response body (camelCase or PascalCase).
 */
export function getAccountDisabledErrorCode(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const body = data as { errorCode?: unknown; ErrorCode?: unknown };
  const raw = body.errorCode ?? body.ErrorCode;
  return typeof raw === 'string' ? raw : undefined;
}

/**
 * Returns true when the axios error is HTTP 403 with account_disabled.
 */
export function isAccountDisabledResponse(error: unknown): boolean {
  const axiosError = error as { response?: { status?: number; data?: unknown } };
  if (axiosError.response?.status !== 403) {
    return false;
  }

  return getAccountDisabledErrorCode(axiosError.response.data) === AccountErrorCodes.AccountDisabled;
}

/**
 * Extracts the user-facing message from a 403 account_disabled response.
 */
export function getAccountDisabledMessage(error: unknown): string {
  const axiosError = error as { response?: { data?: { message?: string; Message?: string } } };
  const data = axiosError.response?.data;
  const message = data?.message ?? data?.Message;
  return (
    message ??
    'Your account has been disabled. If you believe this is a mistake, contact support.'
  );
}
